import React from 'react';

import { GridActionsCellItem } from '@mui/x-data-grid';
import { Popover, Tooltip } from '@mui/material';
import { MoreVert } from '@mui/icons-material';

import { moreActionsLabel } from './moreActionsLabel';
import { ActionCellProps, ActionConfig } from './types';
import { MenuItem } from './styles';
import ConfirmationModal from './ConfirmationModal';

const ActionCell: React.FC<ActionCellProps> = ({ actions, disabled = false, recordName }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [pendingAction, setPendingAction] = React.useState<ActionConfig | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const executeAction = async (action: ActionConfig): Promise<void> => {
    setIsProcessing(true);
    try {
      await action.onClick();
    } finally {
      setIsProcessing(false);
      handleClose();
    }
  };

  const handleActionClick = async (action: ActionConfig): Promise<void> => {
    if (action.confirmation && action.confirmation.type !== 'none') {
      setPendingAction(action);
      handleClose();
    } else {
      await executeAction(action);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (pendingAction) {
      await executeAction(pendingAction);
      setPendingAction(null);
    }
  };

  const handleCancelConfirmation = (): void => {
    setPendingAction(null);
  };

  const visibleActions = actions.filter((action) => action.visible !== false);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <>
      <GridActionsCellItem
        icon={<MoreVert />}
        label={moreActionsLabel(recordName)}
        color="inherit"
        onClick={handleOpen}
        disabled={disabled}
        data-testid="more-vert-button"
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {visibleActions.map((action) => {
          const isDisabled = action.disabled || isProcessing;
          const item = (
            <MenuItem
              key={action.id}
              id={action.id}
              data-testid={action.id}
              onClick={() => handleActionClick(action)}
              disabled={isDisabled}
            >
              {action.label}
            </MenuItem>
          );

          if (action.disabled && action.disabledTooltip) {
            return (
              <Tooltip key={action.id} title={action.disabledTooltip} placement="left">
                <span>{item}</span>
              </Tooltip>
            );
          }

          return item;
        })}
      </Popover>

      {pendingAction?.confirmation && pendingAction.confirmation.type !== 'none' && (
        <ConfirmationModal
          open={Boolean(pendingAction)}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirm}
          type={pendingAction.confirmation.type}
          title={pendingAction.confirmation.title}
          message={pendingAction.confirmation.message}
          confirmText={pendingAction.confirmation.confirmText}
          confirmButtonLabel={pendingAction.confirmation.confirmButtonLabel}
          cancelButtonLabel={pendingAction.confirmation.cancelButtonLabel}
          hideRecoveryWarning={pendingAction.confirmation.hideRecoveryWarning}
          loading={isProcessing}
        />
      )}
    </>
  );
};

export default ActionCell;
